var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Mesh as BABYLON_Mesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.js';
import { LinesMesh as BABYLON_LinesMesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/linesMesh.js';
import { VertexData as BABYLON_VertexData } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.vertexData.js';
import { IVector3 } from '../api.js';
import { Vector4 as BABYLON_Vector4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { MeshBuilder as BABYLON_MeshBuilder } from '@inter-ikea-betrakta-babylonjs/core/Meshes/meshBuilder.js';
import { SceneLines } from './SceneLines.js';
import { RenderingGroupID } from './Viewer.js';
import { Deprecated } from '../utils/Deprecated.js';
import { SceneAssetCachePurgeMode } from './scene/SceneAssetCache.js';
export class MeshManager {
    constructor(viewer, asset_manager, light_system, mark_scene_dirty) {
        this.viewer = viewer;
        this.asset_manager = asset_manager;
        this.light_system = light_system;
        this.mark_scene_dirty = mark_scene_dirty;
        this._scene = viewer.scene;
    }
    create_plane(width, height, uv0, uv1, material_id) {
        var _a, _b, _c;
        width = width || 1.0;
        height = height || 1.0;
        const half_width = width / 2.0;
        const half_height = height / 2.0;
        const positions = [
            -half_width, -half_height, 0,
            half_width, -half_height, 0,
            half_width, half_height, 0,
            -half_width, half_height, 0
        ];
        const normals = [
            0, 0, -1.0,
            0, 0, -1.0,
            0, 0, -1.0,
            0, 0, -1.0
        ];
        uv0 = uv0 || { x: 0.0, y: 0.0, z: 1.0, w: 1.0 };
        const uvs = [
            uv0.z, uv0.w,
            uv0.x, uv0.w,
            uv0.x, uv0.y,
            uv0.z, uv0.y
        ];
        let uvs2;
        if (uv1) {
            uvs2 = [
                uv1.z, uv1.w,
                uv1.x, uv1.w,
                uv1.x, uv1.y,
                uv1.z, uv1.y
            ];
        }
        const indices = [0, 3, 2, 0, 2, 1];
        const vertex_data = new BABYLON_VertexData();
        vertex_data.positions = positions;
        vertex_data.normals = normals;
        vertex_data.uvs = uvs;
        if (uv1) {
            vertex_data.uvs2 = uvs2;
        }
        vertex_data.indices = indices;
        const mesh = new BABYLON_Mesh('', this._scene);
        vertex_data.applyToMesh(mesh);
        const material = material_id === undefined ? null : ((_a = this.asset_manager.materials.get(material_id)) !== null && _a !== void 0 ? _a : null);
        if (material_id !== undefined) {
            mesh.material = (_c = (_b = material === null || material === void 0 ? void 0 : material.material) === null || _b === void 0 ? void 0 : _b.babylon_material) !== null && _c !== void 0 ? _c : null;
        }
        else {
            mesh.material = this.asset_manager.default_material;
        }
        const object = this.viewer.viewer_scene.load_meshes([mesh]);
        if (material_id !== undefined) {
            object.set_material(material_id);
        }
        return object.id;
    }
    create_sphere(segments, diameter, material_id) {
        var _a, _b, _c;
        const mesh = BABYLON_MeshBuilder.CreateSphere('', { segments: segments, diameter: diameter }, this._scene);
        mesh.setIndices(mesh.getIndices().reverse()); // Reverse indices, BABYLON is CW as standard, we want to mirror glTF standard and be CCW
        const material = material_id === undefined ? null : ((_a = this.asset_manager.materials.get(material_id)) !== null && _a !== void 0 ? _a : null);
        if (material_id !== undefined) {
            mesh.material = (_c = (_b = material === null || material === void 0 ? void 0 : material.material) === null || _b === void 0 ? void 0 : _b.babylon_material) !== null && _c !== void 0 ? _c : null;
        }
        else {
            mesh.material = this.asset_manager.default_material;
        }
        const object = this.viewer.viewer_scene.load_meshes([mesh]);
        if (material_id !== undefined) {
            object.set_material(material_id);
        }
        return object.id;
    }
    create_ellipsoid(segments, diameter_x, diameter_y, diameter_z, slice, arc, material_id) {
        var _a, _b, _c;
        const mesh = BABYLON_MeshBuilder.CreateSphere('', { slice: slice, arc: arc, segments: segments, diameterX: diameter_x, diameterY: diameter_y, diameterZ: diameter_z }, this._scene);
        mesh.setIndices(mesh.getIndices().reverse()); // Reverse indices, BABYLON is CW as standard, we want to mirror glTF standard and be CCW
        const material = material_id === undefined ? null : ((_a = this.asset_manager.materials.get(material_id)) !== null && _a !== void 0 ? _a : null);
        if (material_id !== undefined) {
            mesh.material = (_c = (_b = material === null || material === void 0 ? void 0 : material.material) === null || _b === void 0 ? void 0 : _b.babylon_material) !== null && _c !== void 0 ? _c : null;
        }
        else {
            mesh.material = this.asset_manager.default_material;
        }
        const object = this.viewer.viewer_scene.load_meshes([mesh]);
        if (material_id !== undefined) {
            object.set_material(material_id);
        }
        return object.id;
    }
    create_box(width, height, depth, material_id) {
        var _a, _b, _c;
        const options = {
            width: width,
            height: height,
            depth: depth,
            wrap: true, // This ensures that Babylon not "optimize" UV coords
            faceUV: [
                // Set UV-Faces to be compatible with glTF standard.
                // This is probably needed due to babylon being lefthanded by standard. Check if this is needed in a future babylon update (version of time of writing is 4.1)
                new BABYLON_Vector4(1, 1, 0, 0), // side
                new BABYLON_Vector4(1, 1, 0, 0), // side
                new BABYLON_Vector4(1, 1, 0, 0), // side
                new BABYLON_Vector4(1, 1, 0, 0), // side
                new BABYLON_Vector4(1, 1, 0, 0), // top
                new BABYLON_Vector4(1, 1, 0, 0)
            ] // bottom
        }; // options cast to any, since wrap keyword is missing from Babylon types.
        const mesh = BABYLON_MeshBuilder.CreateBox('', options, this._scene);
        mesh.setIndices(mesh.getIndices().reverse()); // Revrse indicies, BABYLON is CW as standard, we want to mirror glTF standard and be
        const material = material_id === undefined ? null : ((_a = this.asset_manager.materials.get(material_id)) !== null && _a !== void 0 ? _a : null);
        if (material !== null) {
            mesh.material = (_c = (_b = material.material) === null || _b === void 0 ? void 0 : _b.babylon_material) !== null && _c !== void 0 ? _c : null;
        }
        else {
            mesh.material = this.asset_manager.default_material;
        }
        const object = this.viewer.viewer_scene.load_meshes([mesh]);
        if (material_id !== undefined) {
            object.set_material(material_id);
        }
        return object.id;
    }
    generate_mesh_buffers(description) {
        var _a, _b, _c, _d, _e;
        const vertex_data = new BABYLON_VertexData();
        vertex_data.positions = description.positions;
        vertex_data.indices = description.indices;
        vertex_data.colors = (_a = description.colors) !== null && _a !== void 0 ? _a : null;
        vertex_data.normals = (_b = description.normals) !== null && _b !== void 0 ? _b : null;
        vertex_data.tangents = (_c = description.tangents) !== null && _c !== void 0 ? _c : null;
        vertex_data.uvs = (_d = description.uv0) !== null && _d !== void 0 ? _d : null;
        vertex_data.uvs2 = (_e = description.uv1) !== null && _e !== void 0 ? _e : null;
        return vertex_data;
    }
    validate_mesh_description(description) {
        if (description.positions.length === 0) {
            throw Error('Bad IMeshDescription: description contains no position data (empty mesh not allowed)');
        }
        if (description.indices && description.indices.length % 3 !== 0) {
            throw Error('Bad IMeshDescription: indices length must be a multiple of 3');
        }
        if (description.positions.length % 3 !== 0) {
            throw Error('Bad IMeshDescription: positions length must be multiple of 3');
        }
        else if ((!description.indices) && description.positions.length % 9 !== 0) {
            // If we don't have indices our positions buffer needs to be a multiple of 9
            // 3 for xyz and 3 again since there are 3 verts for every trianlge
            throw Error('Bad IMeshDescription: positions length must be multiple of 9 when not using an index buffer');
        }
        if (description.normals && description.normals.length % 3 !== 0) {
            throw Error('Bad IMeshDescription: normals length must be multiple of 3');
        }
        if (description.tangents && description.tangents.length % 4 !== 0) {
            throw Error('Bad IMeshDescription: tangents length must be multiple of 4');
        }
        if (description.uv0 && description.uv0.length % 2 !== 0) {
            throw Error('Bad IMeshDescription: uv0 length must be multiple of 2');
        }
        if (description.uv1 && description.uv1.length % 2 !== 0) {
            throw Error('Bad IMeshDescription: uv1 length must be multiple of 2');
        }
    }
    create_custom_mesh_babylon(description) {
        // Make sure that the data we got makes sense, this function throws if it doesn't
        this.validate_mesh_description(description);
        // Create a single mesh
        const vertex_data = this.generate_mesh_buffers(description);
        const mesh = new BABYLON_Mesh('SourceMesh custom', this._scene);
        mesh.renderingGroupId = RenderingGroupID.DEFAULT_RENDERING_GROUP;
        // Fill the mesh with new data
        vertex_data.applyToMesh(mesh);
        return [mesh];
    }
    create_custom_mesh(mesh_material_pairs) {
        const asset = this.viewer.viewer_scene.create_procedural_scene_asset('custom_mesh', SceneAssetCachePurgeMode.INSTANT, new Promise((resolve) => {
            var _a, _b, _c, _d, _e;
            const mesh_promises = [];
            for (let i = 0; i < mesh_material_pairs.length; i++) {
                const mesh_description = mesh_material_pairs[i].description;
                const material_id = mesh_material_pairs[i].material;
                const desc = {
                    positions: mesh_description.positions,
                    indices: mesh_description.indices,
                    colors: (_a = mesh_description.colors) !== null && _a !== void 0 ? _a : null,
                    normals: (_b = mesh_description.normals) !== null && _b !== void 0 ? _b : null,
                    tangents: (_c = mesh_description.tangents) !== null && _c !== void 0 ? _c : null,
                    uv0: (_d = mesh_description.uv0) !== null && _d !== void 0 ? _d : null,
                    uv1: (_e = mesh_description.uv1) !== null && _e !== void 0 ? _e : null
                };
                const created_meshes = this.create_custom_mesh_babylon(desc);
                created_meshes.forEach((mesh) => {
                    const mesh_promise = new Promise((resolve) => {
                        if (material_id !== null && material_id !== undefined) {
                            const asset_material = this.asset_manager.materials.get(material_id);
                            asset_material.ready.then(() => {
                                var _a, _b;
                                mesh.material = (_b = (_a = asset_material.material) === null || _a === void 0 ? void 0 : _a.babylon_material) !== null && _b !== void 0 ? _b : this.asset_manager.default_material;
                                resolve(mesh);
                            });
                        }
                        else {
                            mesh.material = this.asset_manager.default_material;
                            resolve(mesh);
                        }
                    });
                    mesh_promises.push(mesh_promise);
                });
            }
            Promise.all(mesh_promises).then((meshes) => {
                asset.load_meshes(meshes);
                resolve();
            });
        }));
        const object = this.viewer.viewer_scene.create_object(asset, 'custom_mesh', null);
        // This is done to get the old behaviour that your custom meshes material is fetchable through the old material system.
        // If the user has sent many materials, we just give them the first one, similarly to how we work with GLTFs.
        // This isn't pretty, but it gets fixed with the new material system.
        if (mesh_material_pairs.length > 0) {
            object.material = mesh_material_pairs[0].material;
        }
        return object;
    }
    validate_lines_description(description) {
        if (description.indices && description.indices.length % 2 !== 0) {
            throw Error('Bad ILinesDescription: indices is not a multiple of 2');
        }
        if (description.positions.length === 0) {
            throw Error('Bad ILinesDescription: positions must contain data');
        }
        if (description.positions.length % 3 !== 0) {
            throw Error('Bad ILinesDescription: positions length must be a multiple of 3');
        }
        if (description.indices === undefined && description.positions.length % 6 !== 0) {
            throw Error('Bad ILinesDescription: positions length must be a multiple of 6 if no indices are provided');
        }
        if (description.colors.length % 4 !== 0) {
            throw Error('Bad ILinesDescription: colors length must be a multiple of 4');
        }
        if (description.colors.length / 4 !== description.positions.length / 3) {
            throw Error('Bad ILinesDescription: there must be the same amount of primitives in positions and colors');
        }
    }
    generate_lines_buffer(description) {
        const vertex_data = new BABYLON_VertexData();
        vertex_data.positions = description.positions;
        vertex_data.indices = description.indices;
        vertex_data.colors = description.colors;
        return vertex_data;
    }
    create_custom_lines(description) {
        this.validate_lines_description(description);
        const mesh = new BABYLON_LinesMesh('viewer lines primitive', this._scene, undefined, undefined, undefined, true, true);
        mesh.renderingGroupId = RenderingGroupID.DEFAULT_RENDERING_GROUP;
        const vertex_data = this.generate_lines_buffer(description);
        vertex_data.applyToMesh(mesh);
        const object = new SceneLines(mesh, this.viewer);
        object.recalculate_local_bounds(description);
        this.asset_manager.register_scene_lines(object);
        return object;
    }
    update_custom_lines(scene_object, description) {
        if (scene_object instanceof SceneLines) {
            const vertex_data = this.generate_lines_buffer(description);
            vertex_data.applyToMesh(scene_object.mesh);
            scene_object.recalculate_local_bounds(description);
        }
        else {
            throw Error('Tried to custom lines object of the wrong base class');
        }
    }
}
__decorate([
    Deprecated({ new: 'PrimitiveBuilder.create_plane' })
], MeshManager.prototype, "create_plane", null);
__decorate([
    Deprecated({ new: 'PrimitiveBuilder.create_sphere' })
], MeshManager.prototype, "create_sphere", null);
__decorate([
    Deprecated({ new: 'PrimitiveBuilder.create_spherical_dome' })
], MeshManager.prototype, "create_ellipsoid", null);
__decorate([
    Deprecated({ new: 'PrimitiveBuilder.create_box' })
], MeshManager.prototype, "create_box", null);
export function mark_mesh_dirty_for_lightsystem(light_system, mesh) {
    const bs = mesh.getBoundingInfo();
    const bs_center = bs.boundingSphere.centerWorld;
    const bs_radius = bs.boundingSphere.radiusWorld;
    const bounds = { position: new IVector3(bs_center.x, bs_center.y, bs_center.z), radius: bs_radius };
    if (!mesh.metadata) {
        mesh.metadata = {};
    }
    const prev_bounds = mesh.metadata.previous_bounds ? mesh.metadata.previous_bounds : bounds;
    light_system.on_mesh_modified(mesh.uniqueId, prev_bounds, bounds);
    mesh.metadata.previous_bounds = bounds;
}
//# sourceMappingURL=MeshManager.js.map